import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import {
    Typography,
    Grid,
    Box,
    CssBaseline,
    TextField,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import axios from 'axios';
import { global_link } from '../Components/Globallink';
import WebSocketContext from '../Context/WebSocket';

const Dashboard = ({ isDarkMode, setIsDarkMode }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [activeTab2, setActiveTab2] = useState(0);
    const api = axios.create({ baseURL: global_link });
    const authToken = process.env.AUTH_TOKEN;

    const [allcalls, setCalls] = useState(0);
    const [allinbound, setInbound] = useState(0);
    const [alloutbound, setOutbound] = useState(0);
    const [allmissed, setMissed] = useState(0);
    const [allreceived, setReceived] = useState(0);
    const [noanswer, setNoAnswer] = useState(0);
    const [talktime, setTalkTime] = useState('00:00:00');
    const [talktimeout, setTalkTimeOut] = useState('00:00:00');
    const [talktimein, setTalkTimeIn] = useState('00:00:00');
    const [avtalktime, setAvTalkTime] = useState('00:00:00');
    const [avtalktimeout, setAvTalkTimeOut] = useState('00:00:00');
    const [avtalktimein, setAvTalkTimeIn] = useState('00:00:00');
    const [minicalls, setMiniCalls] = useState([]);
    const [minidispositions, setMiniDisp] = useState([]);
    const [stateDate, setStateDate] = useState('');
    const [name, setName] = useState('');

    // ── Use shared context socket — no throwaway socket ───────────────────────
    const { socket } = useContext(WebSocketContext);

    // ── fetchAllData ref — always points to latest version with current stateDate
    // Without this, the socket handler and window event handler close over the
    // initial fetchAllData which has stateDate='' baked in forever. If the user
    // picks a date filter and a call ends, the dashboard would reset to all-time data.
    const fetchAllDataRef = useRef(null);

    const authyToken = localStorage.getItem(authToken);
    const headers = {
        'Authorization': `Bearer ${authyToken}`,
        'Content-Type': 'application/json',
    };

    const handleTabChange = (event, newValue) => setActiveTab(newValue);
    const handleTabChange2 = (event, newValue) => setActiveTab2(newValue);

    const piedata = [
        { name: 'Incoming',  value: parseInt(allinbound)  },
        { name: 'Outgoing',  value: parseInt(alloutbound) },
        { name: 'Missed',    value: parseInt(allmissed)   },
        { name: 'Answered',  value: parseInt(allreceived) },
        { name: 'No Answer', value: parseInt(noanswer)    },
    ];
    const piecolors = ['orange', 'aqua', 'red', '#5CB338', '#7B68EE'];

    // ── fetchCallData — takes current date from closure, not stale capture ───
    const fetchCallData = useCallback(async (endpoint, setState, date = '') => {
        const authyToken = localStorage.getItem(authToken);
        const headers = {
            'Authorization': `Bearer ${authyToken}`,
            'Content-Type': 'application/json',
        };
        try {
            const url = date
                ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}date=${date}`
                : endpoint;
            const response = await api.get(url, { headers, withCredentials: true });
            setState(response.data.total || 0);
        } catch (error) {
            console.error(error);
            setState(0);
        }
    }, [authToken]);

    const getDurationColor = (duration) => {
        if (duration === 'Missed')    return '#ff5722';
        if (duration === 'Busy')      return '#ff9800';
        if (duration === 'Rejected')  return '#f44336';
        if (duration === 'No Answer') return '#9e9e9e';
        return 'inherit';
    };

    const TotalDuration = useCallback(() => {
        const authyToken = localStorage.getItem(authToken);
        const headers = { 'Authorization': `Bearer ${authyToken}`, 'Content-Type': 'application/json' };

        const url    = stateDate ? `/call/gettalktime?date=${stateDate}` : '/call/gettalktime';
        const urlIn  = stateDate ? `/call/gettalktime?search=incoming&date=${stateDate}` : '/call/gettalktime?search=incoming';
        const urlOut = stateDate ? `/call/gettalktime?search=outgoing&date=${stateDate}` : '/call/gettalktime?search=outgoing';

        api.get(url,    { headers, withCredentials: true }).then(r => setTalkTime(r.data.average_talktime)).catch(console.error);
        api.get(urlIn,  { headers, withCredentials: true }).then(r => setTalkTimeIn(r.data.average_talktime)).catch(console.error);
        api.get(urlOut, { headers, withCredentials: true }).then(r => setTalkTimeOut(r.data.average_talktime)).catch(console.error);
    }, [stateDate, authToken]);

    const AverageDuration = useCallback(() => {
        const authyToken = localStorage.getItem(authToken);
        const headers = { 'Authorization': `Bearer ${authyToken}`, 'Content-Type': 'application/json' };

        const url    = stateDate ? `/call/getaveragetalktime?date=${stateDate}` : '/call/getaveragetalktime';
        const urlIn  = stateDate ? `/call/getaveragetalktime?search=incoming&date=${stateDate}` : '/call/getaveragetalktime?search=incoming';
        const urlOut = stateDate ? `/call/getaveragetalktime?search=outgoing&date=${stateDate}` : '/call/getaveragetalktime?search=outgoing';

        api.get(url,    { headers, withCredentials: true }).then(r => setAvTalkTime(r.data.average_talktime)).catch(console.error);
        api.get(urlIn,  { headers, withCredentials: true }).then(r => setAvTalkTimeIn(r.data.average_talktime)).catch(console.error);
        api.get(urlOut, { headers, withCredentials: true }).then(r => setAvTalkTimeOut(r.data.average_talktime)).catch(console.error);
    }, [stateDate, authToken]);

    const userInfo = useCallback(() => {
        const authyToken = localStorage.getItem(authToken);
        const headers = { 'Authorization': `Bearer ${authyToken}`, 'Content-Type': 'application/json' };
        api.get('/user/user', { headers, withCredentials: true })
            .then(r => setName(r.data.user_name))
            .catch(console.error);
    }, [authToken]);

    const fetchMinicalls = useCallback(() => {
        const authyToken = localStorage.getItem(authToken);
        const headers = { 'Authorization': `Bearer ${authyToken}`, 'Content-Type': 'application/json' };
        const url = stateDate
            ? `/call/getcalllogsdash/?limit=150&date=${stateDate}`
            : '/call/getcalllogsdash/?limit=150';
        api.get(url, { headers, withCredentials: true })
            .then(r => setMiniCalls(r.data))
            .catch(console.error);
    }, [stateDate, authToken]);

    const fetchMinidispostns = useCallback(() => {
        const authyToken = localStorage.getItem(authToken);
        const headers = { 'Authorization': `Bearer ${authyToken}`, 'Content-Type': 'application/json' };
        const url = stateDate
            ? `/disposition/dash/?limit=150&date=${stateDate}`
            : '/disposition/dash/?limit=150';
        api.get(url, { headers, withCredentials: true })
            .then(r => setMiniDisp(r.data))
            .catch(console.error);
    }, [stateDate, authToken]);

    // ── fetchAllData — stable callback, all deps included ────────────────────
    const fetchAllData = useCallback(async () => {
        await Promise.all([
            fetchCallData('/call/getcalllogscount', setCalls, stateDate),
            fetchCallData('/call/getcalllogscount?search=incoming', setInbound, stateDate),
            fetchCallData('/call/getcalllogscount?search=outgoing', setOutbound, stateDate),
            fetchCallData('/call/getcalllogscount?search=Missed', setMissed, stateDate),
            fetchCallData('/call/getcalllogscount?search=received', setReceived, stateDate),
            fetchCallData('/call/getcalllogscount?search=No Answer', setNoAnswer, stateDate),
        ]);
        userInfo();
        fetchMinicalls();
        fetchMinidispostns();
        TotalDuration();
        AverageDuration();
    }, [
        stateDate,
        fetchCallData,
        userInfo,
        fetchMinicalls,
        fetchMinidispostns,
        TotalDuration,
        AverageDuration,
    ]);

    // Keep ref in sync with the latest fetchAllData whenever stateDate or any dep changes
    useEffect(() => {
        fetchAllDataRef.current = fetchAllData;
    }, [fetchAllData]);

    // Run on mount and whenever stateDate changes
    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    // ── Socket: NEW_CALL_LOG_UPDATE — uses ref, re-syncs on reconnect ─────────
    useEffect(() => {
        if (!socket) return;

        const handleUpdate = () => fetchAllDataRef.current?.();

        socket.on('NEW_CALL_LOG_UPDATE', handleUpdate);
        socket.on('connect', handleUpdate); // re-fetch on reconnect

        return () => {
            socket.off('NEW_CALL_LOG_UPDATE', handleUpdate);
            socket.off('connect', handleUpdate);
        };
    }, [socket]); // fetchAllDataRef intentionally excluded — using ref

    // ── Window event from DialBar (same-tab call end) ─────────────────────────
    useEffect(() => {
        const handleUpdateCallsData = () => fetchAllDataRef.current?.();
        window.addEventListener('updateCallsDataEvent', handleUpdateCallsData);
        return () => window.removeEventListener('updateCallsDataEvent', handleUpdateCallsData);
    }, []); // fetchAllDataRef intentionally excluded — using ref

    const handleDateChange = (event) => setStateDate(event.target.value);

    const copyPhoneNumber = (call_from, call_to) => {
        const phoneNumber = call_from.length > 5 ? call_from : call_to;
        window.dispatchEvent(new CustomEvent('copyPhoneNumber', { detail: phoneNumber }));
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <CssBaseline />
            <Box sx={{ paddingBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant='h5' sx={{ marginBottom: '15px' }}>
                    Hi, {name}
                </Typography>
                <TextField
                    label="Date"
                    type="date"
                    value={stateDate}
                    onChange={handleDateChange}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: '20%', minWidth: '120px' }}
                />
            </Box>

            <Grid container spacing={0.7}>
                <Grid item sx={{ flex: '1' }}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px', padding: '20px', borderBottom: '3px solid darkblue' }}>
                        <Box><Typography variant='h5'>All Calls</Typography></Box>
                        <Box><Typography variant='h4' fontWeight='600'>{parseInt(allcalls)}</Typography></Box>
                    </Box>
                </Grid>
                <Grid item sx={{ flex: '1' }}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px', padding: '20px', borderBottom: '3px solid orange' }}>
                        <Box><Typography variant='h5'>Incoming</Typography></Box>
                        <Box><Typography variant='h4' fontWeight='600'>{parseInt(allinbound)}</Typography></Box>
                    </Box>
                </Grid>
                <Grid item sx={{ flex: '1' }}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px', padding: '20px', borderBottom: '3px solid aqua' }}>
                        <Box><Typography variant='h5'>Outgoing</Typography></Box>
                        <Box><Typography variant='h4' fontWeight='600'>{parseInt(alloutbound)}</Typography></Box>
                    </Box>
                </Grid>
                <Grid item sx={{ flex: '1' }}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px', padding: '20px', borderBottom: '3px solid #5CB338' }}>
                        <Box><Typography variant='h5'>Answered</Typography></Box>
                        <Box><Typography variant='h4' fontWeight='600'>{parseInt(allreceived)}</Typography></Box>
                    </Box>
                </Grid>
                <Grid item sx={{ flex: '1' }}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px', padding: '20px', borderBottom: '3px solid red' }}>
                        <Box><Typography variant='h5'>Missed</Typography></Box>
                        <Box><Typography variant='h4' fontWeight='600'>{parseInt(allmissed)}</Typography></Box>
                    </Box>
                </Grid>
                <Grid item sx={{ flex: '1' }}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px', padding: '20px', borderBottom: '3px solid #7B68EE' }}>
                        <Box><Typography variant='h5'>No Answer</Typography></Box>
                        <Box><Typography variant='h4' fontWeight='600'>{parseInt(noanswer)}</Typography></Box>
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={1} sx={{ marginTop: '15px' }}>
                <Grid item xs={12} md={8}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px', height: '57vh', overflow: 'auto' }}>
                        <Box position="sticky" top="-1px">
                            <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" sx={{ padding: '2px', bgcolor: 'background.paper' }}>
                                <Tab label="Recent Calls" />
                                <Tab label="Dispositions" />
                            </Tabs>
                        </Box>

                        {activeTab === 0 && (
                            <TableContainer>
                                <Table sx={{ fontSize: '5.1rem !important' }} size="medium">
                                    <TableHead sx={{ bgcolor: 'background.table', width: '100%' }}>
                                        <TableRow>
                                            <TableCell>Source</TableCell>
                                            <TableCell>Destination</TableCell>
                                            <TableCell>Duration</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {minicalls.map((calllogs) => (
                                            <TableRow
                                                key={calllogs.id}
                                                sx={{ cursor: 'pointer', color: calllogs.call_duration === 'Missed' ? '#ff5722 !important' : '' }}
                                                onClick={() => copyPhoneNumber(calllogs.call_from, calllogs.call_to)}
                                            >
                                                <TableCell>{calllogs.call_from}</TableCell>
                                                <TableCell>{calllogs.call_to}</TableCell>
                                                <TableCell sx={{ cursor: 'pointer', color: `${getDurationColor(calllogs.call_duration)} !important` }}>
                                                    {calllogs.call_duration}
                                                </TableCell>
                                                <TableCell>{calllogs.call_type}</TableCell>
                                                <TableCell>{calllogs.call_time}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}

                        {activeTab === 1 && (
                            <TableContainer>
                                <Table sx={{ fontSize: '5.1rem !important' }} size="medium">
                                    <TableHead sx={{ bgcolor: 'background.table', width: '100%' }}>
                                        <TableRow>
                                            <TableCell>Owner</TableCell>
                                            <TableCell>Contact</TableCell>
                                            <TableCell>Disposition</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Date</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {minidispositions.map((dispo) => (
                                            <TableRow key={dispo.id} sx={{ cursor: 'pointer' }} onClick={() => copyPhoneNumber(dispo.disposition_contact)}>
                                                <TableCell>{dispo.user_name}</TableCell>
                                                <TableCell sx={{ cursor: 'pointer' }}>{dispo.disposition_contact}</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{dispo.disposition}</TableCell>
                                                <TableCell>{dispo.call_type}</TableCell>
                                                <TableCell>{dispo.disposition_time}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid rgba(207, 211, 212, 0.2)`, borderRadius: '5px', height: '57vh' }}>
                        <Tabs value={activeTab2} onChange={handleTabChange2} variant="fullWidth" sx={{ padding: '2px' }}>
                            <Tab label="Call Stats" />
                            <Tab label="Talk Time" />
                            <Tab label="Average T.T" />
                        </Tabs>

                        {activeTab2 === 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '49vh' }}>
                                <PieChart width={300} height={300}>
                                    <Pie data={piedata} dataKey="value" nameKey="name" outerRadius={100} innerRadius={70} label paddingAngle={5}>
                                        {piedata.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={piecolors[index]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value, name) => [`${name}: ${value}`]} />
                                </PieChart>
                            </Box>
                        )}

                        {activeTab2 === 1 && (
                            <Box sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex', paddingBottom: '28px' }}>
                                <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                                    <Grid xs={12} p={1}><Typography variant='h5' textAlign="center" sx={{ fontWeight: '400', marginTop: '10px' }}>Total Talk Time</Typography></Grid>
                                    <Grid xs={6}><Typography variant='h6' sx={{ fontWeight: '600', textAlign: 'center', marginTop: '10px' }}>Outgoing <br /> {talktimeout}</Typography></Grid>
                                    <Grid xs={6}><Typography variant='h6' sx={{ fontWeight: '600', textAlign: 'center', marginTop: '10px' }}>Incoming <br /> {talktimein}</Typography></Grid>
                                    <Grid xs={12} p={1}><Typography variant='h2' textAlign="center" sx={{ fontWeight: 700, marginTop: '30px', marginBottom: '80px' }}>{talktime}</Typography></Grid>
                                </Grid>
                            </Box>
                        )}

                        {activeTab2 === 2 && (
                            <Box sx={{ justifyContent: 'center', alignItems: 'center', display: 'flex', paddingBottom: '28px' }}>
                                <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                                    <Grid xs={12} p={1}><Typography variant='h5' textAlign="center" sx={{ fontWeight: '400', marginTop: '10px' }}>Average Talk Time</Typography></Grid>
                                    <Grid xs={6}><Typography variant='h6' sx={{ fontWeight: '600', textAlign: 'center', marginTop: '10px' }}>Outgoing <br /> {avtalktimeout}</Typography></Grid>
                                    <Grid xs={6}><Typography variant='h6' sx={{ fontWeight: '600', textAlign: 'center', marginTop: '10px' }}>Incoming <br /> {avtalktimein}</Typography></Grid>
                                    <Grid xs={12} p={1}><Typography variant='h2' textAlign="center" sx={{ fontWeight: 700, marginTop: '30px', marginBottom: '80px' }}>{avtalktime}</Typography></Grid>
                                </Grid>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default Dashboard;